import React, { useState, useMemo } from 'react';
import { groupByMonthYear } from '../../utils/groupByMonthYear';

/**
 * Tabla con filas agrupadas por mes/año (multiperiodo) y secciones colapsables.
 * @param {Array} data - Lista de ítems
 * @param {(item) => string|Date|null} getDateForGrouping - Fecha para agrupar (ej. item => item.start_date)
 * @param {(item) => React.ReactNode} renderRow - Función que devuelve <tr>...</tr> por ítem
 * @param {React.ReactNode} headerRow - <tr> con los <th> de la tabla (solo contenido del tr)
 * @param {number} columnCount - Número de columnas (para colspan del título del grupo)
 * @param {boolean} defaultCollapsed - Si true, los grupos inician colapsados
 * @param {string} className - Clases adicionales para la tabla
 */
const TableWithMonthGroups = ({
  data,
  getDateForGrouping,
  renderRow,
  headerRow,
  colgroup = null,
  columnCount,
  defaultCollapsed = false,
  className = '',
  wrapperClassName = 'overflow-x-auto',
  groupHeaderClassName = 'px-6 py-2.5 text-sm font-semibold text-gray-700',
  emptyMessage = 'No hay registros',
}) => {
  const [collapsed, setCollapsed] = useState({});
  const groups = useMemo(
    () => groupByMonthYear(data || [], getDateForGrouping),
    [data, getDateForGrouping]
  );

  const toggle = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isCollapsed = (key) => {
    if (defaultCollapsed && collapsed[key] === undefined) return true;
    return !!collapsed[key];
  };

  if (!data?.length) {
    return (
      <div className={wrapperClassName}>
        <table className={`w-full divide-y divide-gray-200 ${className}`}>
          {colgroup}
          <thead className="bg-gray-50">{headerRow}</thead>
          <tbody>
            <tr>
              <td colSpan={columnCount} className="px-6 py-8 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <table className={`w-full divide-y divide-gray-200 ${className}`}>
        {colgroup}
        <thead className="bg-gray-50">{headerRow}</thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {groups.map((group) => {
            const collapsedState = isCollapsed(group.key);
            return (
              <React.Fragment key={group.key}>
                <tr
                  className="bg-gray-100/80 hover:bg-gray-100 border-y border-gray-200"
                  onClick={() => toggle(group.key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(group.key)}
                  aria-expanded={!collapsedState}
                >
                  <td colSpan={columnCount} className={groupHeaderClassName}>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded" aria-hidden>
                        {collapsedState ? (
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </span>
                      <span>{group.label}</span>
                      <span className="text-gray-500 font-normal">
                        ({group.items.length} {group.items.length === 1 ? 'registro' : 'registros'})
                      </span>
                    </div>
                  </td>
                </tr>
                {!collapsedState && group.items.map((item) => renderRow(item))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TableWithMonthGroups;
